import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";
import { URL } from "../config";
import AddPackagePurchaseModal from "./AddPackagePurchasedCustomers";
import { useOutletContext } from "react-router-dom";


const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-GB");
};

const PackagePurchaseList = () => {
  const { setLoading } = useOutletContext();

  const [dataSource, setDataSource] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editPurchase, setEditPurchase] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [packageSubTypes, setPackageSubTypes] = useState([]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const [custRes, pkgRes, typeRes, subTypeRes, purchasesRes] =
        await Promise.all([
          axios.get(`${URL}/customers`),
          axios.get(`${URL}/packages`),
          axios.get(`${URL}/packagetypes`),
          axios.get(`${URL}/packagesubtypes`),
          axios.get(`${URL}/packagepurchasecustomers`),
        ]);

      setCustomers(custRes.data);
      setPackages(pkgRes.data);
      setPackageTypes(typeRes.data);
      setPackageSubTypes(subTypeRes.data);
      setDataSource(purchasesRes.data);
    } catch (err) {
      toast.error("Failed to fetch data");
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getCustomerName = (customerId) =>
    customers.find((c) => c.CustomerId === customerId)?.CustomerName || "-";

  const getPackageDisplay = (packageId) => {
    const pkg = packages.find((p) => p.packageId === packageId);
    if (!pkg) return "-";
    const type =
      packageTypes.find((t) => t.packageTypeId === pkg.fkpackageTypeId)
        ?.packageTypeName || "-";
    const subType =
      packageSubTypes.find((st) => st.packageSubTypeId === pkg.fkpackageSubTypeId)
        ?.packageSubTypeName || "-";
    return `${type} | ${subType} | ${pkg.packageTitle}`;
  };


  const handleEdit = (item) => {
    setEditPurchase(item);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this purchase?")) return;
    try {
      await axios.delete(`${URL}/packagepurchasecustomers/${id}`);
      toast.success("Deleted successfully");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Package Purchases");

    worksheet.columns = [
      { header: "SrNo.", key: "purchaseId", width: 10 },
      { header: "Customer", key: "customerName", width: 25 },
      { header: "Package", key: "packageDisplay", width: 40 },
      { header: "No of Cameras", key: "noOfCamera", width: 15 },
      { header: "Total Price", key: "totalPrice", width: 15 },
      { header: "Start Date", key: "packageStartDate", width: 15 },
      { header: "End Date", key: "packageEndDate", width: 15 },
    ];

    dataSource.forEach((item) => {
      worksheet.addRow({
        purchaseId: item.purchaseId,
        customerName: getCustomerName(item.fkcustomerId),
        packageDisplay: getPackageDisplay(item.fkpackageId),
        noOfCamera: item.noOfCamera,
        totalPrice: item.totalPrice,
        packageStartDate: formatDate(item.packageStartDate),
        packageEndDate: formatDate(item.packageEndDate),
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "PackagePurchases.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(18);
    doc.text("Package Purchases", 40, 40);

    autoTable(doc, {
      startY: 60,
      head: [["SrNo.", "Customer", "Package", "No of Cameras", "Total Price", "Start Date", "End Date"]],
      body: dataSource.map((item) => [
        item.purchaseId,
        getCustomerName(item.fkcustomerId),
        getPackageDisplay(item.fkpackageId),
        item.noOfCamera,
        item.totalPrice,
        formatDate(item.packageStartDate),
        formatDate(item.packageEndDate),
      ]),
      headStyles: { fillColor: [23, 66, 83], textColor: "#fff" },
      styles: { fontSize: 10, cellPadding: 6 },
      theme: "grid",
    });

    doc.save("PackagePurchases.pdf");
  };


  const handlePrint = () => {
  const tableRows = dataSource
    .map(
      (item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${getCustomerName(item.fkcustomerId)}</td>
        <td>${getPackageDisplay(item.fkpackageId)}</td>
        <td>${item.noOfCamera}</td>
        <td>${item.totalPrice}</td>
        <td>${formatDate(item.packageStartDate)}</td>
        <td>${formatDate(item.packageEndDate)}</td>
      </tr>
    `
    )
    .join("");

  const style = `
    <style>
      @media print {
        @page { size: landscape; margin: 18mm; }
        body { -webkit-print-color-adjust: exact !important; }
      }

      body {
        font-family: Arial, Helvetica, sans-serif;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }

      th {
        background: #174253;
        color: #fff;
        padding: 10px;
        text-align: left;
        border-bottom: 2px solid #174253;
      }

      td {
        padding: 10px;
        border-top: 1px solid #dcdcdc;
        border-bottom: 1px solid #dcdcdc;
      }

      tr:nth-child(even) td {
        background: #f5f5f5;
      }

      h1 {
        text-align: center;
        color: #174253;
        margin-bottom: 20px;
        font-size: 22px;
      }
    </style>
  `;

  const html = `
    <html>
      <head>
        <title>Package Purchases</title>
        ${style}
      </head>
      <body>
        <h1>Package Purchases</h1>

        <table>
          <thead>
            <tr>
              <th>SrNo.</th>
              <th>Customer</th>
              <th>Package</th>
              <th>No of Cameras</th>
              <th>Total Price</th>
              <th>Start Date</th>
              <th>End Date</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
};


  const columns = useMemo(
    () => [
      {
        header: "Actions",
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Edit">
              <IconButton onClick={() => handleEdit(row.original)}>
                <EditIcon sx={{ color: "#174253" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton onClick={() => handleDelete(row.original.purchaseId)}>
                <DeleteIcon sx={{ color: "#d32f2f" }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { accessorKey: "purchaseId", header: "ID" },
      {
        accessorKey: "fkcustomerId",
        header: "Customer",
        Cell: ({ row }) => getCustomerName(row.original.fkcustomerId),
      },
     {
  accessorKey: "fkpackageId",
  header: "Package",
  size: 320, 
  Cell: ({ row }) => getPackageDisplay(row.original.fkpackageId),
}
,
      { accessorKey: "noOfCamera", header: "No of Cameras" },
      { accessorKey: "totalPrice", header: "Total Price" },
      {
        accessorKey: "packageStartDate",
        header: "Start Date",
        Cell: ({ row }) => formatDate(row.original.packageStartDate),
      },
      {
        accessorKey: "packageEndDate",
        header: "End Date",
        Cell: ({ row }) => formatDate(row.original.packageEndDate),
      },
    ],
    [customers, packages, packageTypes, packageSubTypes]
  );

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
     
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", fontWeight: 700, fontSize: 22, color: "#174253", letterSpacing: 0.5, borderLeft: "5px solid #174253", pl: 1.5 }}
        >
          Package Purchases
        </Box>
        <Button
           sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
            "&:hover": { background: "linear-gradient(90deg, #174253, #0b8793)" },
          }}
          onClick={() => {
            setEditPurchase(null);
            setModalVisible(true);
          }}
        >
          Add Purchase
        </Button>
      </Box>

      <Box sx={{ background: "#fff", borderRadius: "0 0 15px 15px" }}>
        <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
          <div style={{ position: "absolute", top: 10, right: 20, left: 20, display: "flex", justifyContent: "space-between", alignItems: "center", zIndex: 2, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tooltip title="Download Excel">
                <IconButton onClick={handleDownloadExcel} sx={{ backgroundColor: "#d4f1d4", color: "#174253", "&:hover": { backgroundColor: "#b5e3b5" } }}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton onClick={handleDownloadPDF} sx={{ backgroundColor: "#ffe2e2", color: "#d32f2f", "&:hover": { backgroundColor: "#ffcccc" } }}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} sx={{ backgroundColor: "#e3f2fd", color: "#174253", "&:hover": { backgroundColor: "#cde4fa" } }}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </div>
            <TextField
              size="small"
              placeholder="Search ..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#174253" }} /></InputAdornment> }}
              sx={{ width: { xs: 200, sm: 250 }, "& .MuiOutlinedInput-root": { borderRadius: 25, height: 38, backgroundColor: "#fff", boxShadow: "0 0 6px rgba(0,0,0,0.1)", "& fieldset": { borderColor: "#d0d7de" }, "&:hover fieldset": { borderColor: "#174253" } } }}
            />
          </div>
        </div>

<MaterialReactTable
  columns={columns}
  data={
    [...dataSource] 
      .reverse()    
      .filter((item) => {
      
        const searchStr = `
          ${getCustomerName(item.fkcustomerId)}
          ${getPackageDisplay(item.fkpackageId)}
          ${item.noOfCamera}
          ${item.totalPrice}
          ${formatDate(item.packageStartDate)}
          ${formatDate(item.packageEndDate)}
        `.toLowerCase();

        return searchStr.includes(globalFilter.toLowerCase());
      })
  }

  enableColumnActions={false}
  enableDensityToggle={false}
  enableFullScreenToggle={false}
  enableColumnFilters={false}
  enableHiding={false}
  enableGlobalFilter={false}

  muiTablePaperProps={{
    sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
  }}

  muiTableContainerProps={{
    sx: {
      backgroundColor: "#fff",
      width: "100%",
      overflowX: "auto",
      borderRadius: "12px",
      "&::-webkit-scrollbar": { height: "6px" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#e0e3ea",
        borderRadius: "10px",
      },
    },
  }}

  muiTableHeadCellProps={{
    sx: {
      backgroundColor: "#e0e0e0ff",
      color: "#393e4f",
      fontWeight: 600,
      fontSize: "16px",
      borderBottom: "0.5px solid #e3e5eb",
      borderRight: "none",
      borderLeft: "none",
      textAlign: "left",
      height: "63px",
      paddingTop: "23px",
      marginTop: "15px",
    },
  }}

  muiTableBodyRowProps={{
    sx: {
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#eff6fe" },
      "& td": {
        fontSize: { xs: "12px", sm: "14px" },
        p: "8px",
        borderBottom: "0.5px solid #e3e5eb",
        borderRight: "none",
        borderLeft: "none",
        height: "54px",
      },
    },
  }}

  initialState={{
    pagination: {
      pageIndex: 0,
      pageSize: 100,
    },
  }}
/>

      {modalVisible && (
        <AddPackagePurchaseModal
          data={editPurchase}
          onClose={() => setModalVisible(false)}
          refresh={fetchData}
        />
      )}
    </Box>
    </Box>
  );
};

export default PackagePurchaseList;
